// Compact Filter Header - Single line with dropdowns
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilter,
  faSortAmountDown,
  faCircleCheck,
  faChevronDown,
  faImage,
  faVideo,
  faMusic,
  faMicrophone,
  faEllipsisH,
  faList,
  faFileAlt,
  faCheckCircle,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FilterOptions, FilterStats } from '../services/promptFilterService';
import styles from './CompactFilterHeader.module.css';

interface CompactFilterHeaderProps {
  totalCount: number;
  filterOptions: FilterOptions;
  filterStats: FilterStats;
  onContentTypeChange: (contentType: FilterOptions['contentType']) => void;
  onStatusChange: (status: FilterOptions['status']) => void;
  onSortChange: (sortBy: FilterOptions['sortBy']) => void;
  onReset: () => void;
}

export const CompactFilterHeader: React.FC<CompactFilterHeaderProps> = ({
  totalCount,
  filterOptions,
  filterStats,
  onContentTypeChange,
  onStatusChange,
  onSortChange,
  onReset
}) => {
  const { t } = useTranslation('promptGenerator');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = {
    filter: useRef<HTMLDivElement>(null),
    sort: useRef<HTMLDivElement>(null),
    status: useRef<HTMLDivElement>(null)
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && dropdownRefs[openDropdown as keyof typeof dropdownRefs]?.current) {
        const ref = dropdownRefs[openDropdown as keyof typeof dropdownRefs].current;
        if (ref && !ref.contains(event.target as Node)) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // NOTE: Filter IDs are NOT translated - they are used for backend filtering
  // Only the labels are translated for display
  const contentTypeFilters = [
    { id: 'all', label: t('promptGenerator.filters.allTypes'), icon: faList, count: filterStats.total },
    { id: 'image', label: t('promptGenerator.filters.images'), icon: faImage, count: filterStats.image },
    { id: 'video', label: t('promptGenerator.filters.videos'), icon: faVideo, count: filterStats.video },
    { id: 'music', label: t('promptGenerator.filters.music'), icon: faMusic, count: filterStats.music },
    { id: 'sfx', label: t('promptGenerator.filters.soundFx'), icon: faMicrophone, count: filterStats.sfx },
    { id: 'narration', label: t('promptGenerator.filters.voice'), icon: faMicrophone, count: filterStats.narration },
    { id: 'character', label: t('promptGenerator.filters.characters'), icon: faUser, count: filterStats.character },
    { id: 'others', label: t('promptGenerator.filters.others'), icon: faEllipsisH, count: filterStats.others }
  ];

  const statusFilters = [
    { id: 'all', label: t('promptGenerator.filters.allStatus'), icon: faList, count: filterStats.total },
    { id: 'completed', label: t('promptGenerator.filters.complete'), icon: faCheckCircle, count: filterStats.completed },
    { id: 'draft', label: t('promptGenerator.filters.drafts'), icon: faFileAlt, count: filterStats.draft }
  ];

  const sortOptions = [
    { value: 'date-desc', label: t('promptGenerator.filters.newestFirst') },
    { value: 'date-asc', label: t('promptGenerator.filters.oldestFirst') },
    { value: 'sequence', label: t('promptGenerator.filters.bySequence') },
    { value: 'type', label: t('promptGenerator.filters.byType') },
    { value: 'title', label: t('promptGenerator.filters.byTitle') }
  ];

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const getCurrentFilterLabel = () => {
    const filter = contentTypeFilters.find(f => f.id === filterOptions.contentType);
    return filter?.label || t('promptGenerator.filters.filter');
  };

  const getCurrentStatusLabel = () => {
    const status = statusFilters.find(s => s.id === filterOptions.status);
    return status?.label || t('promptGenerator.filters.status');
  };

  const getCurrentSortLabel = () => {
    const sort = sortOptions.find(s => s.value === filterOptions.sortBy);
    return sort?.label || t('promptGenerator.filters.sort');
  };

  return (
    <div className={styles.filterHeader}>
      <div className={styles.rightSection}>
        {/* Filter Dropdown */}
        <div className={styles.dropdownContainer} ref={dropdownRefs.filter}>
          <button
            className={`${styles.dropdownTrigger} ${filterOptions.contentType !== 'all' ? styles.active : ''}`}
            onClick={() => toggleDropdown('filter')}
          >
            <FontAwesomeIcon icon={faFilter} />
            <span>{getCurrentFilterLabel()}</span>
            <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
          </button>
          
          {openDropdown === 'filter' && (
            <div className={styles.dropdown}>
              {contentTypeFilters.map(filter => (
                <button
                  key={filter.id}
                  className={`${styles.dropdownItem} ${filterOptions.contentType === filter.id ? styles.selected : ''}`}
                  onClick={() => {
                    onContentTypeChange(filter.id as FilterOptions['contentType']);
                    setOpenDropdown(null);
                  }}
                >
                  <FontAwesomeIcon icon={filter.icon} />
                  <span>{filter.label}</span>
                  <span className={styles.count}>{filter.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sort Dropdown */}
        <div className={styles.dropdownContainer} ref={dropdownRefs.sort}>
          <button
            className={`${styles.dropdownTrigger} ${filterOptions.sortBy !== 'date-desc' ? styles.active : ''}`}
            onClick={() => toggleDropdown('sort')}
          >
            <FontAwesomeIcon icon={faSortAmountDown} />
            <span>{getCurrentSortLabel()}</span>
            <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
          </button>
          
          {openDropdown === 'sort' && (
            <div className={styles.dropdown}>
              {sortOptions.map(option => (
                <button
                  key={option.value}
                  className={`${styles.dropdownItem} ${filterOptions.sortBy === option.value ? styles.selected : ''}`}
                  onClick={() => {
                    onSortChange(option.value as FilterOptions['sortBy']);
                    setOpenDropdown(null);
                  }}
                >
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Status Dropdown */}
        <div className={`${styles.dropdownContainer} ${styles.rightAligned}`} ref={dropdownRefs.status}>
          <button
            className={`${styles.dropdownTrigger} ${filterOptions.status !== 'all' ? styles.active : ''}`}
            onClick={() => toggleDropdown('status')}
          >
            <FontAwesomeIcon icon={faCircleCheck} />
            <span>{getCurrentStatusLabel()}</span>
            <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
          </button>
          
          {openDropdown === 'status' && (
            <div className={`${styles.dropdown} ${styles.dropdownRight}`}>
              {statusFilters.map(status => (
                <button
                  key={status.id}
                  className={`${styles.dropdownItem} ${filterOptions.status === status.id ? styles.selected : ''}`}
                  onClick={() => {
                    onStatusChange(status.id as FilterOptions['status']);
                    setOpenDropdown(null);
                  }}
                >
                  <FontAwesomeIcon icon={status.icon} />
                  <span>{status.label}</span>
                  <span className={styles.count}>{status.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
